/**
 * Static description registries extracted from JSON Schema definitions.
 *
 * These registries are the single source of truth for property descriptions
 * shared between the MCP server `help()` output and the demo props panel.
 * Safety tests enforce that every key in the JSON Schema has a corresponding
 * entry here — adding a schema key without a description causes a test failure.
 */

import { dataFieldLabel, optionLabel } from './tool-labels.js';

/** Mapping key descriptions (from schema $defs/mapping.properties). */
export const MAPPING_DOCS: Readonly<Record<string, string>> = {
  x: 'X-axis / category field',
  y: 'Y-axis value field(s)',
  label: 'Label field (pie/funnel/gantt)',
  value: 'Value field (pie/funnel/heatmap/histogram)',
  color: 'Color grouping field (scatter/gantt)',
  size: 'Size encoding field (scatter bubble)',
  opacity: 'Opacity encoding field (scatter bubble)',
  axis: 'Axis field (radar indicators)',
  total: 'Total-flag field — truthy rows draw as absolute totals (waterfall)',
  start: 'Interval start field — number or date-like (gantt)',
  end: 'Interval end field — number or date-like (gantt)',
  columns: 'Column definitions (table)',
  primary: 'Primary text field (list)',
  secondary: 'Secondary text field (list)',
  icon: 'Icon letter field (list)',
  avatar: 'Avatar image URL field (list)',
  trailing: 'Trailing value field (list)',
  badge: 'Badge/tag field (list)',
};

/** Field definition property descriptions (from schema $defs/fieldDefinition). */
export const FIELD_PROP_DOCS: Readonly<Record<string, string>> = {
  field: 'Key name in submitted data',
  label: 'Display label',
  type: 'Input type',
  required: 'Must be filled before submit',
  placeholder: 'Placeholder text',
  options: 'Choices for select/multiselect/radio/checkbox — string, or { value, label } to submit a value other than the text shown',
  minLength: 'Minimum character length',
  maxLength: 'Maximum character length',
  pattern: 'Custom regex pattern',
  rows: 'Textarea visible rows',
  min: 'Minimum value',
  max: 'Maximum value',
  step: 'Number/range step increment',
  message: 'Custom validation error message',
};

/**
 * Well-known data field descriptions.
 * Unlike schema-bound registries, these cover common keys used across widgets.
 * Not enforced by schema tests — new keys appear with type only until added here.
 */
export const DATA_FIELD_DOCS: Readonly<Record<string, string>> = {
  // metric / stat-group
  value: 'The primary value to display',
  label: 'Display label or category name',
  unit: 'Value unit suffix',
  prefix: 'Text before the value',
  suffix: 'Text after the value',
  change: 'Delta / change amount',
  trend: 'Trend direction (up, down, flat)',
  // content
  content: 'Body text or markdown content',
  message: 'Message body text',
  level: 'Severity level (info, warning, error, success)',
  title: 'Heading or title text',
  src: 'Source URL (image)',
  alt: 'Alt text (image)',
  caption: 'Caption text',
  // progress
  max: 'Maximum bound value',
  // treemap / general
  name: 'Node or item name',
  children: 'Nested child nodes',
  // kv / header
  language: 'Programming language identifier',
  key: 'Key or label identifier',
  text: 'Display text content',
  // steps
  status: 'Step status (done, active, pending, error)',
  description: 'Detailed description text',
  // rating
  count: 'Number of reviews or votes',
  // video / citation
  poster: 'Video poster image URL',
  url: 'Link URL',
  snippet: 'Excerpt or summary text',
  source: 'Source name or publisher',
  // math
  expression: 'LaTeX math expression string',
  // chart data
  group: 'Group / category identifier',
  min: 'Minimum stat value',
  q1: 'First quartile (25th percentile)',
  median: 'Median (50th percentile)',
  q3: 'Third quartile (75th percentile)',
};

/**
 * Well-known options descriptions.
 * Covers widget-specific rendering options used across the catalog.
 */
export const OPTION_DOCS: Readonly<Record<string, string>> = {
  // gauge / progress
  min: 'Minimum range value',
  max: 'Maximum range value',
  unit: 'Display unit string',
  thresholds: 'Color threshold breakpoints',
  label: 'Label template ({value}, {percent})',
  format: "Value format hint — 'number', 'number:2' (fixed decimals), 'percent', 'currency:EUR' … in the widget's locale (gauge/progress default: number)",
  // chart
  smooth: 'Smooth curves (line/area)',
  stack: 'Stack series (bar/line/area)',
  horizontal: 'Horizontal orientation (bar)',
  donut: 'Donut style (pie)',
  colors: 'Custom color palette',
  colorRange: 'Heatmap color gradient',
  histogram: 'Histogram mode (bar)',
  bins: 'Number of histogram bins (auto: Sturges rule)',
  referenceLines: 'Reference/threshold lines',
  categories: 'Row order top to bottom; listed rows show even without intervals (gantt)',
  series: "Per-series overrides in mapping.y order [{label?, color?, lineStyle?: 'solid'|'dashed'|'dotted'|{ECharts lineStyle}, symbol?, type?, yAxisIndex?}]",
  conditionalStyles: 'Per-point conditional styling rules [{field, above?, below?, color, symbol?, symbolSize?}]',
  subtitle: 'Static subtitle text (gauge)',
  step: 'Step interpolation (line)',
  legend: 'Show/hide legend',
  grid: 'Show/hide grid lines',
  animate: 'Enable/disable animation',
  showLabel: 'Show/hide data labels',
  xFormat: 'X-axis label format {type?, prefix?, suffix?, currency?, decimals?}',
  yFormat: 'Y-axis label format {type?, prefix?, suffix?, currency?, decimals?}',
  valueAxisZero: 'Value axis reaches zero (default); false spans the data — for values far from zero (bar/line/area)',
  xAxisType: "'value' — points at their numeric x (uneven times or distances), not evenly spaced categories (line/area)",
  // table / list
  pageSize: 'Rows per page',
  compact: 'Compact display mode',
  searchable: 'Enable search filter',
  locale: 'Locale for formatting/validation',
  // code
  lineNumbers: 'Show/hide line numbers (default true)',
  highlight: 'Line numbers to highlight (array)',
  maxHeight: 'Maximum height with scroll (e.g. "300px")',
  wrap: 'Word wrap long lines (default false)',
  // citation
  numbered: 'Show numbered badges (default true)',
  // rating
  interactive: 'Allow user input (default false)',
  icon: 'Icon type (star, heart, thumb)',
  // video
  autoplay: 'Auto-play on load (default false)',
  controls: 'Show playback controls (default true)',
  loop: 'Loop playback (default false)',
  muted: 'Start muted (default false, auto when autoplay)',
  // gallery
  aspectRatio: 'Image aspect ratio (e.g. "1:1", "16:9", default auto)',
  // math
  displayMode: 'Block mode with centered large formula (default false = inline)',
  // compose / kv / actions / divider / steps
  layout: 'Layout mode (stack, row, grid, horizontal, vertical, wrap, column)',
  columns: 'Number of grid columns',
  widths: 'Column width ratios (number[], "auto", "stretch")',
  spacing: 'Spacing size (small, default, large)',
  card: 'Render inside a card container with shadow and border',
};

/**
 * What an option's value is.
 *
 * `type` uses the notation of `DataFieldInfo.type`. `enum` lists a choice's values and `default` is what
 * the widget uses when the option is left out — present only where the widget's code fixes one.
 */
export interface OptionTypeInfo {
  readonly type: string;
  readonly enum?: readonly string[];
  readonly default?: unknown;
}

const choice = (values: readonly string[], defaultValue?: string): OptionTypeInfo => ({
  type: values.map(v => `"${v}"`).join(' | '),
  enum: values,
  ...(defaultValue !== undefined && { default: defaultValue }),
});

/** Value types of the options in OPTION_DOCS, as most widgets read them. */
export const OPTION_TYPES: Readonly<Record<string, OptionTypeInfo>> = {
  min: { type: 'number' },
  max: { type: 'number' },
  unit: { type: 'string' },
  thresholds: { type: '{ to: number, color: string, label?: string }[]' },
  label: { type: 'string' },
  smooth: { type: 'boolean', default: false },
  stack: { type: 'boolean', default: false },
  horizontal: { type: 'boolean', default: false },
  donut: { type: 'boolean', default: false },
  colors: { type: 'string[]' },
  colorRange: { type: 'string[]' },
  histogram: { type: 'boolean', default: false },
  bins: { type: 'number' },
  referenceLines: { type: 'object[]' },
  categories: { type: 'string[]' },
  series: { type: 'object[]' },
  conditionalStyles: { type: 'object[]' },
  subtitle: { type: 'string' },
  format: { type: 'string' },
  step: { type: 'boolean | "start" | "middle" | "end"' },
  legend: { type: 'boolean', default: true },
  grid: { type: 'boolean', default: true },
  animate: { type: 'boolean', default: true },
  showLabel: { type: 'boolean' },
  xFormat: { type: 'object' },
  yFormat: { type: 'object' },
  valueAxisZero: { type: 'boolean', default: true },
  xAxisType: { type: '"category" | "value"', default: 'category' },
  pageSize: { type: 'number' },
  compact: { type: 'boolean', default: false },
  searchable: { type: 'boolean', default: false },
  locale: { type: 'string' },
  lineNumbers: { type: 'boolean', default: true },
  highlight: { type: 'number[]' },
  maxHeight: { type: 'string' },
  wrap: { type: 'boolean', default: false },
  numbered: { type: 'boolean', default: true },
  interactive: { type: 'boolean', default: false },
  icon: choice(['star', 'heart', 'thumb'], 'star'),
  autoplay: { type: 'boolean', default: false },
  controls: { type: 'boolean', default: true },
  loop: { type: 'boolean', default: false },
  muted: { type: 'boolean', default: false },
  aspectRatio: { type: 'string', default: 'auto' },
  displayMode: { type: 'boolean', default: false },
  layout: { type: 'string' },
  columns: { type: 'number' },
  widths: { type: '(number | "auto" | "stretch")[]' },
  spacing: choice(['small', 'default', 'large'], 'default'),
  card: { type: 'boolean', default: false },
};

/** Where an option's type, choices, default or meaning differ for one widget. */
const WIDGET_OPTION_OVERRIDES: Readonly<Record<string, Readonly<Record<string, Partial<OptionTypeInfo> & { desc?: string }>>>> = {
  gauge: { min: { default: 0 }, max: { default: 100 } },
  progress: { min: { default: 0 }, max: { default: 100 } },
  rating: { max: { default: 5, desc: 'Number of icons' }, label: { desc: 'Label text' } },
  kv: { layout: choice(['vertical', 'horizontal', 'grid'], 'vertical'), columns: { default: 2, desc: 'Number of grid columns (grid layout)' } },
  steps: { layout: choice(['vertical', 'horizontal'], 'vertical') },
  actions: { layout: choice(['wrap', 'column'], 'wrap') },
  divider: { label: { desc: 'Text on the line' } },
};

/** An option a widget reads: its key, short name, description and value type. */
export interface OptionInfo extends OptionTypeInfo {
  readonly key: string;
  /** A short name for a form label ("Minimum") — in the requested locale (see `getToolLabels`). */
  readonly label: string;
  readonly desc: string;
}

/**
 * The options `widget` reads, each with its name in `locale` (resolved as a widget's locale is), its
 * description and its value type (`OPTION_TYPES`, adjusted for the widget).
 */
export function getWidgetOptions(widget: string, locale?: string): OptionInfo[] {
  const keys = WIDGET_OPTIONS[widget] ?? [];
  return keys.map(key => {
    const { desc, ...override } = WIDGET_OPTION_OVERRIDES[widget]?.[key] ?? {};
    return { key, label: optionLabel(widget, key, locale), desc: desc ?? OPTION_DOCS[key] ?? '', ...OPTION_TYPES[key], ...override };
  });
}

/** Action property descriptions (from schema $defs/action). */
export const ACTION_PROP_DOCS: Readonly<Record<string, string>> = {
  label: 'Button text',
  action: 'Action identifier (submit, cancel, navigate, custom)',
  style: 'Visual style (primary, danger, default)',
  disabled: 'Whether disabled',
  url: 'URL for navigate action',
};

// ── Per-Widget Registries ──

/** Data field descriptor for well-known widget data properties. */
export interface DataFieldInfo {
  readonly key: string;
  /** A short name for a form label ("Value") — English in `WIDGET_DATA_FIELDS`, localized by `getWidgetDataFields`. */
  readonly label: string;
  readonly type: string;
  readonly desc: string;
  readonly required?: boolean;
  /** The values of a field whose `type` is a union of string literals (`"up" | "down" | "flat"`). */
  readonly enum?: readonly string[];
}

/** `"a" | "b"` → `['a', 'b']`; anything that is not purely string literals → `undefined`. */
function literalUnion(type: string): string[] | undefined {
  const parts = type.split('|').map(part => part.trim());
  const values = parts.map(part => /^"([^"]*)"$/.exec(part)?.[1]);
  return values.every(v => v !== undefined) ? (values as string[]) : undefined;
}

/** Fills in each field's English name and, for a union of string literals, its `enum`. */
function withNamesAndEnums(fields: Record<string, readonly Omit<DataFieldInfo, 'label' | 'enum'>[]>): Record<string, readonly DataFieldInfo[]> {
  return Object.fromEntries(
    Object.entries(fields).map(([widget, list]) => [
      widget,
      list.map(field => {
        const values = literalUnion(field.type);
        return { ...field, label: dataFieldLabel(widget, field.key, 'en'), ...(values && { enum: values }) };
      }),
    ]),
  );
}

/** Relevant option keys per widget type. Only lists keys from OPTION_DOCS. */
export const WIDGET_OPTIONS: Readonly<Record<string, readonly string[]>> = {
  'chart.bar': ['stack', 'horizontal', 'histogram', 'colors', 'series', 'referenceLines', 'conditionalStyles', 'xFormat', 'yFormat', 'valueAxisZero', 'legend', 'grid', 'animate', 'showLabel'],
  'chart.line': ['smooth', 'stack', 'step', 'colors', 'series', 'referenceLines', 'conditionalStyles', 'xFormat', 'yFormat', 'valueAxisZero', 'xAxisType', 'legend', 'grid', 'animate', 'showLabel'],
  'chart.area': ['smooth', 'stack', 'colors', 'series', 'conditionalStyles', 'xFormat', 'yFormat', 'valueAxisZero', 'xAxisType', 'legend', 'grid', 'animate', 'showLabel'],
  'chart.pie': ['donut', 'colors', 'legend', 'animate', 'showLabel'],
  'chart.scatter': ['colors', 'referenceLines', 'conditionalStyles', 'xFormat', 'yFormat', 'legend', 'grid', 'animate', 'showLabel'],
  'chart.radar': ['colors', 'legend', 'animate', 'showLabel'],
  'chart.heatmap': ['colorRange', 'legend', 'grid', 'animate', 'showLabel'],
  'chart.box': ['colors', 'legend', 'grid', 'animate'],
  'chart.funnel': ['colors', 'legend', 'animate', 'showLabel'],
  'chart.waterfall': ['colors', 'legend', 'grid', 'animate', 'showLabel'],
  'chart.treemap': ['colors', 'animate', 'showLabel'],
  'chart.histogram': ['bins', 'colors', 'referenceLines', 'xFormat', 'yFormat', 'legend', 'grid', 'animate'],
  'chart.gantt': ['categories', 'colors', 'referenceLines', 'xFormat', 'legend', 'grid', 'animate', 'showLabel'],
  'metric': ['format'],
  'stat-group': ['format'],
  'gauge': ['min', 'max', 'unit', 'thresholds', 'label', 'subtitle', 'format'],
  'progress': ['min', 'max', 'unit', 'thresholds', 'label', 'format'],
  'table': ['pageSize', 'compact', 'searchable'],
  'list': ['compact'],
  'form': ['locale'],
  'confirm': [],
  'markdown': [],
  'image': [],
  'callout': [],
  // `layout` and `columns` are top-level spec fields of compose, not options.
  'compose': ['widths', 'card'],
  'kv': ['layout', 'columns'],
  'code': ['lineNumbers', 'highlight', 'maxHeight', 'wrap'],
  'citation': ['compact', 'numbered'],
  'status': [],
  'steps': ['layout', 'compact'],
  'rating': ['max', 'interactive', 'icon', 'label'],
  'video': ['autoplay', 'controls', 'loop', 'muted'],
  'gallery': ['columns', 'aspectRatio'],
  'math': ['displayMode'],
  'actions': ['layout'],
  'divider': ['label', 'spacing'],
  'header': [],
};

/**
 * Well-known data fields per widget type.
 * Charts/table/list use user-defined fields so they are omitted.
 */
export const WIDGET_DATA_FIELDS: Readonly<Record<string, readonly DataFieldInfo[]>> = withNamesAndEnums({
  'metric': [
    { key: 'value', type: 'number | string', desc: 'Primary value', required: true },
    { key: 'label', type: 'string', desc: 'Display label' },
    { key: 'unit', type: 'string', desc: 'Value unit suffix' },
    { key: 'prefix', type: 'string', desc: 'Text before value' },
    { key: 'suffix', type: 'string', desc: 'Text after value' },
    { key: 'change', type: 'number', desc: 'Delta amount' },
    { key: 'trend', type: '"up" | "down" | "flat"', desc: 'Trend direction' },
    { key: 'icon', type: 'string', desc: 'Emoji or short text icon' },
    { key: 'description', type: 'string', desc: 'Sub-label below value' },
  ],
  'stat-group': [
    { key: 'label', type: 'string', desc: 'Display label', required: true },
    { key: 'value', type: 'number', desc: 'Primary value', required: true },
    { key: 'unit', type: 'string', desc: 'Value unit suffix' },
    { key: 'prefix', type: 'string', desc: 'Text before value' },
    { key: 'suffix', type: 'string', desc: 'Text after value' },
    { key: 'change', type: 'number', desc: 'Delta amount' },
    { key: 'trend', type: '"up" | "down" | "flat"', desc: 'Trend direction' },
    { key: 'icon', type: 'string', desc: 'Emoji or short text icon' },
    { key: 'description', type: 'string', desc: 'Sub-label below value' },
  ],
  'gauge': [
    { key: 'value', type: 'number', desc: 'Current value on the arc', required: true },
  ],
  'progress': [
    { key: 'value', type: 'number', desc: 'Current value', required: true },
    { key: 'max', type: 'number', desc: 'Maximum bound (default 100)' },
  ],
  'markdown': [
    { key: 'content', type: 'string', desc: 'Markdown text', required: true },
  ],
  'image': [
    { key: 'src', type: 'string', desc: 'Image URL', required: true },
    { key: 'alt', type: 'string', desc: 'Alt text' },
    { key: 'caption', type: 'string', desc: 'Caption text' },
  ],
  'callout': [
    { key: 'message', type: 'string', desc: 'Body text', required: true },
    { key: 'title', type: 'string', desc: 'Optional heading' },
    { key: 'level', type: '"info" | "warning" | "error" | "success"', desc: 'Severity' },
  ],
  'code': [
    { key: 'content', type: 'string', desc: 'Code text content', required: true },
    { key: 'language', type: 'string', desc: 'Language identifier (e.g. javascript, python, sql)' },
  ],
  'math': [
    { key: 'expression', type: 'string', desc: 'LaTeX math expression', required: true },
  ],
  'header': [
    { key: 'text', type: 'string', desc: 'Heading text', required: true },
    { key: 'level', type: '1 | 2 | 3', desc: 'Heading level (default 2)' },
  ],
  'video': [
    { key: 'src', type: 'string', desc: 'Video URL', required: true },
    { key: 'poster', type: 'string', desc: 'Poster image URL' },
    { key: 'alt', type: 'string', desc: 'Accessible description' },
    { key: 'caption', type: 'string', desc: 'Caption text' },
    { key: 'tracks', type: 'Array<{ src, srclang?, label?, kind?, default? }>', desc: 'Subtitle / caption tracks (WebVTT). kind: subtitles (default) | captions | descriptions | chapters | metadata' },
  ],
  'gallery': [
    { key: 'src', type: 'string', desc: 'Image URL', required: true },
    { key: 'alt', type: 'string', desc: 'Alt text' },
    { key: 'caption', type: 'string', desc: 'Caption text' },
  ],
  'steps': [
    { key: 'label', type: 'string', desc: 'Step label', required: true },
    { key: 'status', type: '"done" | "active" | "pending" | "error"', desc: 'Step status (default pending)' },
    { key: 'description', type: 'string', desc: 'Step detail text' },
    { key: 'icon', type: 'string', desc: 'Custom icon (emoji/text) override' },
  ],
  'rating': [
    { key: 'value', type: 'number', desc: 'Current rating value' },
    { key: 'max', type: 'number', desc: 'Scale maximum (default 5)' },
    { key: 'count', type: 'number', desc: 'Number of reviews' },
  ],
  'citation': [
    { key: 'title', type: 'string', desc: 'Citation title', required: true },
    { key: 'url', type: 'string', desc: 'Link URL' },
    { key: 'snippet', type: 'string', desc: 'Excerpt text' },
    { key: 'source', type: 'string', desc: 'Source name' },
  ],
  'status': [
    { key: 'label', type: 'string', desc: 'Status label' },
    { key: 'value', type: 'string', desc: 'Status value', required: true },
    { key: 'level', type: '"info" | "success" | "warning" | "error" | "neutral"', desc: 'Severity level (default info)' },
  ],
});

/** The data fields `widget` reads (`WIDGET_DATA_FIELDS`), each with its name in `locale` (resolved as a widget's locale is). */
export function getWidgetDataFields(widget: string, locale?: string): DataFieldInfo[] {
  return (WIDGET_DATA_FIELDS[widget] ?? []).map(field => ({ ...field, label: dataFieldLabel(widget, field.key, locale) }));
}

/** Auto-inference hints — tells LLM what can be omitted. */
export const WIDGET_INFERENCE: Readonly<Record<string, string>> = {
  'chart.bar': 'mapping omittable. First string → x, number fields → y.',
  'chart.line': 'mapping omittable. First string → x, numbers → y. Date-like preferred for x.',
  'chart.area': 'mapping omittable. Same as line — first string → x, numbers → y.',
  'chart.pie': 'mapping omittable. First string → label, first number → value.',
  'chart.scatter': 'mapping omittable. First two numbers → x, y. color/size optional.',
  'chart.radar': 'mapping omittable. First string → axis, numbers → series values.',
  'chart.heatmap': 'mapping recommended. x, y (categories), value (intensity).',
  'chart.box': 'mapping recommended. x (group), y mapped to [min, q1, median, q3, max].',
  'chart.funnel': 'mapping omittable. First string → label, first number → value.',
  'chart.waterfall': 'mapping omittable. First string → x, first number → y.',
  'chart.treemap': 'No mapping. data is [{name, value, children?}].',
  'chart.histogram': 'Flat number[] or objects with single numeric field → frequency distribution',
  'chart.gantt': 'mapping omittable. First string → y (row), first two number/date-like fields → start, end, next string → label. color groups intervals.',
  'metric': 'No mapping needed. data is {value, label?, unit?, change?, trend?}.',
  'stat-group': 'No mapping needed. data is [{label, value, ...}]. Optional: icon (emoji), description (sub-label).',
  'gauge': 'No mapping needed. data is {value}. Set min/max/unit in options.',
  'progress': 'No mapping needed. data is {value, max?}. Set label in options.',
  'table': 'mapping.columns omittable — auto-inferred from data keys.',
  'list': 'mapping omittable if data has primary/secondary keys. Otherwise set mapping.',
  'form': 'Uses fields[] or formdown, not data/mapping. data provides defaults.',
  'confirm': 'Uses title, description, actions. data is optional.',
  'markdown': 'No mapping. data is {content: "markdown string"}.',
  'image': 'No mapping. data is {src, alt?, caption?}.',
  'callout': 'No mapping. data is {message, title?, level?}.',
  'compose': 'Uses children[] and layout. Each child is a widget spec.',
  'kv': 'No mapping. data is {key: val, ...} or [{key, value}]. layout in options.',
  'code': 'No mapping. data is {content, language?}. lineNumbers/highlight/maxHeight/wrap in options.',
  'citation': 'No mapping. data is {title, url?, snippet?, source?} or array of those. compact/numbered in options.',
  'status': 'No mapping. data is {label, value, level?} or array of those. level: info/success/warning/error/neutral.',
  'video': 'No mapping. data is {src, poster?, alt?, caption?, tracks?}. autoplay/controls/loop/muted in options.',
  'gallery': 'No mapping. data is [{src, alt?, caption?}]. columns/aspectRatio in options.',
  'steps': 'No mapping. data is [{label, status?, description?, icon?}]. status: done/active/pending/error. icon: emoji/text override. layout: vertical/horizontal.',
  'rating': 'No mapping. data is {value?, max?}. options: interactive, icon (star/heart/thumb), max, label.',
  'math': 'No mapping. data is {expression}. Renders LaTeX via KaTeX. displayMode in options.',
  'actions': 'No data. Uses actions[] array. layout in options (wrap/column).',
  'divider': 'No data. label/spacing in options.',
  'header': 'data is {text, level?}. level defaults to 2.',
};

/** Events emitted per widget category. */
export const WIDGET_EVENTS: Readonly<Record<string, readonly string[]>> = {
  chart: ['select'],
  table: ['select'],
  list: ['select'],
  form: ['submit', 'change', 'action'],
  confirm: ['submit', 'action'],
  citation: ['action'],
  rating: ['submit'],
  actions: ['action'],
};

/** Get the event types emitted by a widget. Resolves `chart.*` → `chart`. */
export function getWidgetEvents(widget: string): readonly string[] {
  return WIDGET_EVENTS[widget] ?? WIDGET_EVENTS[widget.split('.')[0]] ?? [];
}
